import { createChartOption } from 'features/Integrations/features/ConnectedDevices/utils/chart.utils';
import { getSeriesType } from './deviceGraph.utils';

/** @typedef {import('../types/UsgsTypes').GraphDataset} GraphDataset */
/** @typedef {import('../types/UsgsTypes').GraphDatasetMeta} GraphDatasetMeta */
/** @typedef {import('../types/UsgsTypes').GraphDataPoint} GraphDataPoint */
/** @typedef {import('../types/UsgsTypes').TooltipFormatter} TooltipFormatter */

// This file contains utility functions for displaying USGS device graphs. Data manipulation
// functions are in `usgsDeviceGraphUtils.js`, while this file focuses on display-related
// transformations and ECharts option construction.

/**
 * Convert lightweight GraphDataset entries into ECharts series items.
 * @param {GraphDataset[]} [datasets=[]]
 * @param {{type?: string, smooth?: boolean, area?: boolean, showSymbol?: boolean, connectNulls?: boolean}} [options]
 * @returns {Array<object>} ECharts series configuration objects
 */
const mapUSGSToEChartsSeries = (
  datasets = [],
  options = {
    type: 'line',
    smooth: false,
    area: false,
    showSymbol: false,
    connectNulls: false,
  },
) => {
  if (!Array.isArray(datasets) || datasets.length === 0) return [];

  const { type, smooth, area, showSymbol, connectNulls } = options;

  return datasets.map((dataset) => {
    // Use device id (from dataset._meta.id) as the internal series `name` so
    // multiple devices sharing the same alias are unique
    const internalName =
      dataset?._meta?.id != null
        ? String(dataset._meta.id)
        : dataset.label || dataset.name || 'series';
    const name = internalName;
    const color = dataset.color || (dataset._meta && dataset._meta.color) || undefined;

    const series = {
      name,
      type,
      data: Array.isArray(dataset.data)
        ? dataset.data.map((pt) => {
            const x = pt && pt[0] != null ? Number(pt[0]) : null;
            const y = pt && pt[1] !== undefined ? pt[1] : null;
            return [x, y];
          })
        : [],
      showSymbol,
      smooth,
      connectNulls,
      symbol: 'circle',
      symbolSize: (value) => {
        const timestamp = Array.isArray(value) ? value[0] : null;
        const raw = dataset?._meta?.byTimestampMap?.get(timestamp);
        return raw?.did != null || raw?.ts != null ? 6 : 0;
      },
      itemStyle: {},
      lineStyle: {},
      _meta: dataset._meta,
    };

    if (color) {
      series.itemStyle.color = color;
      series.lineStyle.color = color;
    }

    if (area) {
      series.areaStyle = {};
    }

    return series;
  });
};

/**
 * Build a tooltip formatter function for ECharts that reads raw measurement
 * values from `dataset._meta.byTimestampMap` and formats values according to `unitType`.
 *
 * @param {Array<GraphDataset>} datasets
 * @param {boolean} [unitType=false]
 * @returns {TooltipFormatter}
 */
export const buildUSGSTooltipFormatter = (datasets, unitType = false) => {
  return (params) => {
    const items = Array.isArray(params) ? params : [params];
    if (!items.length) return null;

    const ts = items[0].axisValue ?? (Array.isArray(items[0].value) ? items[0].value[0] : null);
    const label = ts ? new Date(ts).toLocaleString(undefined, { timeZoneName: 'short' }) : '';
    // Build HTML with: timestamp, then for each series show alias, then colored marker + parameter name and value
    const parts = [];
    parts.push(`<div class="usgs-tooltip-ts"><strong>${label}</strong></div>`);

    items.forEach((pointParam) => {
      const dataset = datasets[pointParam.seriesIndex] || {};
      const raw = dataset?._meta?.byTimestampMap?.get(ts);

      // Alias: prefer dataset._meta.alias, fall back to dataset.label or seriesName
      const alias = dataset?._meta?.alias || dataset.label || pointParam.seriesName || 'USGS';

      // Parameter name and value
      let paramName = '';
      let display = '';
      if (raw) {
        paramName = raw.parameter_name || '';
        const rawValue = raw.value != null ? Number(raw.value) : NaN;
        if (!isNaN(rawValue)) {
          if (unitType && raw.alt_value != null) {
            display = `${raw.alt_value} ${raw.alt_unit ?? ''}`.trim();
          } else {
            display = `${rawValue} ${raw.unit_of_measure ?? ''}`.trim();
          }
        } else {
          display = raw.value ?? '';
        }
        if (raw.did == null && raw.ts == null) {
          display = `${display} (no datasheet)`;
        }
      } else {
        paramName = pointParam.seriesName || '';
        const y = Array.isArray(pointParam.value) ? pointParam.value[1] : pointParam.value;
        display = y != null ? String(y) : '';
      }

      // tooltip block: alias on its own line, then marker + parameter name and value
      parts.push(
        `<div class="usgs-tooltip-series">
          <div class="usgs-tooltip-alias" style="display:-webkit-box; -webkit-box-orient:vertical; -webkit-line-clamp:2; overflow:hidden; text-overflow:ellipsis;">${alias}</div>
          <div class="usgs-tooltip-line">${pointParam.marker || ''} <span class="usgs-tooltip-param">${paramName}</span>: <span class="usgs-tooltip-value">${display}</span></div>
        </div>`,
      );
    });

    return `<div class="echart-tooltip-content" style="max-width:360px;white-space:normal;overflow-wrap:anywhere;word-break:break-word">${parts.join('')}</div>`;
  };
};

/**
 * Add a `byTimestampMap` to each dataset `_meta` for quick lookup by timestamp.
 *
 * @param {Array<GraphDataset>} datasets
 * @returns {Array<GraphDataset>} datasets augmented with `_meta.byTimestampMap`
 */
export const buildAlignedDatasets = (datasets) => {
  if (!Array.isArray(datasets) || datasets.length === 0) return [];
  return datasets.map((dataset) => {
    const rawPoints = dataset?._meta?.data || [];
    const byTimestampMap = new Map(rawPoints.map((point) => [point.ts, point]));
    return { ...dataset, _meta: { ...(dataset._meta || {}), byTimestampMap } };
  });
};

/**
 * Build ECharts series array from graph datasets given a selected parameter.
 * @param {Array<GraphDataset>} datasets
 * @param {string} selectedDataType
 * @returns {Array<object>} ECharts series
 */
export const buildSeriesFromDatasets = (datasets, selectedDataType) => {
  const seriesType = getSeriesType(selectedDataType);
  return mapUSGSToEChartsSeries(datasets, {
    type: seriesType,
    smooth: false,
    area: false,
    showSymbol: false,
    connectNulls: true,
  });
};

/**
 * Create a formatter for x-axis timestamp labels.
 * @returns {(value: number|string|Date) => string}
 */
export const buildXAxisLabelFormatter = () => {
  const formatter = new Intl.DateTimeFormat([], {
    year: '2-digit',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return (value) => formatter.format(new Date(value));
};

/**
 * Compute a human readable Y-axis label for the selected parameter, attempting
 * to detect unit of measure from dataset metadata and optionally convert units.
 *
 * @param {Array<GraphDataset>} datasets
 * @param {string} selectedParameterName
 * @param {boolean} unitType
 * @returns {string|null}
 */
export const computeYAxisLabel = (datasets, selectedParameterName, unitType) => {
  if (!datasets || datasets.length === 0 || !selectedParameterName) return null;

  let measurement = null;
  for (const dataset of datasets) {
    measurement =
      dataset?._meta?.data?.find((m) => m.parameter_name === selectedParameterName) ??
      dataset?._meta?.data?.[0] ??
      null;
    if (measurement) break;
  }

  if (!measurement) return selectedParameterName;

  const displayUnit =
    unitType && measurement.alt_unit ? measurement.alt_unit : measurement.unit_of_measure || '';

  return displayUnit ? `${selectedParameterName} (${displayUnit})` : selectedParameterName;
};

/**
 * Build chart option object for `BaseChart` from the chart model.
 * @param {{title?: string, labels: string[], startTime?: number|string|Date, endTime?: number|string|Date, series: Array<object>, tooltipFormatter?: TooltipFormatter, xAxisLabelFormatter?: (v:any)=>string, yAxisLabel?: string|null, markLineData?: Array<any>}} params
 * @returns {object} Chart option compatible with `createChartOption`.
 */
export const buildChartOptionFromModel = ({
  title,
  labels,
  startTime,
  endTime,
  series,
  tooltipFormatter,
  xAxisLabelFormatter,
  yAxisLabel,
  markLineData = [],
}) => {
  const option = createChartOption({
    title: title,
    titleTop: 8,
    xAxisLabels: labels,
    minValue: startTime,
    maxValue: endTime,
    series,
    gridPadding: {
      left: '10%',
      right: '2%',
      top: '26%',
      bottom: '18%',
    },
    tooltipFormatter: tooltipFormatter || undefined,
    xAxisRotate: 0,
    xAxisLabelFontSize: 10,
    xAxisLabelHideOverlap: true,
    xAxisLabelInterval: 'auto',
    xAxisLabelType: 'time',
    xAxisLabelFormatter,
    dataZoomEnabled: true,
    markLineData: markLineData,
    yAxisLabel: yAxisLabel,
  });

  // Attach legend formatter so series that share the same alias still show
  // separate legend entries (internal series names use device ids).
  try {
    option.legend = option.legend || {};
    option.legend.formatter = (name) => {
      const s = (option.series || []).find((ser) => String(ser.name) === String(name));
      if (!s) return name;
      return s._meta?.alias || s.name;
    };
  } catch (e) {
    // no-op on formatter setup failure
  }

  return option;
};

/**
 * Compute the current and alternate unit strings for the unit-toggle button label.
 *
 * @param {Array<GraphDataset>} datasets
 * @param {string} selectedDataType - Currently selected parameter name.
 * @param {boolean} unitType - true = converted unit is active.
 * @returns {{current: string, other: string}|null}
 */
export const computeUnitDisplay = (datasets, selectedDataType, unitType) => {
  for (const dataset of datasets) {
    const point =
      dataset?._meta?.data?.find((m) => m.parameter_name === selectedDataType) ??
      dataset?._meta?.data?.[0];
    if (point?.unit_of_measure) {
      return unitType && point.alt_unit ? point.alt_unit : point.unit_of_measure;
    }
  }
  return null;
};
